// pages/Sales/Contract/ViewContractAmendment.jsx
import React, { useEffect, useRef, useState } from "react";
import { Container, Row } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import axios from "axios";
import moment from "moment";
import configure from "configure";
import Loader from "pages/Separate/Loader";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import zithasLogo from "../../../assets/images/zithas-dark-logo.png";
import Swal from "sweetalert2";
import zithasSign from "../../../assets/images/signature.png";

const STATUS_BADGE = {
  draft: { label: "Draft", bg: "#6c757d" },
  pending_approval: { label: "Pending Internal Approval", bg: "#f1b44c" },
  approved_internal: { label: "Approved Internally", bg: "#556ee6" },
  sent_to_client: { label: "Awaiting Client Signature", bg: "#f1b44c" },
  signed: { label: "Signed", bg: "#34c38f" },
  rejected: { label: "Rejected", bg: "#f46a6a" },
};

const ViewContractAmendment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [amendment, setAmendment] = useState(null);
  const pdfRef = useRef();

  document.title = "View Amendment | Zithas CRM";

  useEffect(() => {
    fetchAmendment();
  }, [id]);

  const fetchAmendment = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${configure.nodeApi}/contract_amendment/view/${id}`,
      );
      if (res.data.success) {
        setAmendment(res.data.data);
      } else {
        Swal.fire({
          icon: "error",
          text: res.data.message || "Amendment not found",
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", text: "Failed to load amendment" });
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    try {
      setLoading(true);
      const pdf = new jsPDF("p", "mm", "a4");
      const element = pdfRef.current;
      const canvas = await html2canvas(element);
      const imgData = canvas.toDataURL("image/png");
      const width = pdf.internal.pageSize.getWidth();
      const height = (canvas.height * width) / canvas.width;
      const totalPages = Math.ceil(height / pdf.internal.pageSize.getHeight());
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        const startY = page * pdf.internal.pageSize.getHeight();
        pdf.addImage(imgData, "PNG", 0, -startY, width, height);
      }
      pdf.save(`Amendment-${amendment.amendment_no}.pdf`);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const printAmendment = () => {
    let printContents = document.getElementById("amendmentViewPrint").innerHTML;
    let originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
  };

  if (!amendment && !isLoading) return null;

  const badge = amendment
    ? STATUS_BADGE[amendment.status] || STATUS_BADGE.draft
    : null;

  return (
    <React.Fragment>
      {isLoading && <Loader />}
      <div className="page-content mb-3">
        <Container fluid>
          <Breadcrumbs title="Contract" breadcrumbItem="View Amendment" />

          {amendment && (
            <Row>
              <div className="col-lg-9">
                <div className="d-flex justify-content-end gap-2 mb-3">
                  <button
                    className="btn btn-sm btn-success"
                    onClick={printAmendment}
                  >
                    <i className="fas fa-print me-1" /> Print
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={generatePDF}
                  >
                    <i className="fas fa-file-download me-1" /> Download
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => navigate(-1)}
                  >
                    <i className="dripicons-cross me-1" /> Back
                  </button>
                </div>

                <div
                  id="amendmentViewPrint"
                  ref={pdfRef}
                  className="bg-white shadow"
                  style={{ padding: "100px 110px", boxSizing: "border-box" }}
                >
                  <div className="pb-2 text-center">
                    <img src={zithasLogo} height="60" alt="zithasLogo" />
                  </div>

                  <div className="text-center mb-3">
                    <span
                      className="badge"
                      style={{
                        background: "#752a97",
                        color: "#fff",
                        fontSize: "13px",
                        padding: "6px 16px",
                        borderRadius: "20px",
                      }}
                    >
                      Contract Amendment ·{" "}
                      {amendment.amendment_ref || amendment.amendment_no}
                    </span>
                  </div>

                  <div
                    style={{ textAlign: "justify" }}
                    className="Features"
                    dangerouslySetInnerHTML={{
                      __html: (amendment.document_html || "")
                        .replaceAll("{", "")
                        .replaceAll("}", ""),
                    }}
                  />

                 
                  {amendment.status === "signed" && (
                    <Row className="mt-4 justify-content-between">
                      <div className="col-4">
                        <img
                          src={zithasSign}
                          height="50"
                          className="mb-2"
                          alt="Zithas signature"
                        />
                        <h5 className="mb-0 mt-2">Julius Gohil</h5>
                        <p className="mb-0">Managing Director</p>
                        <p className="mb-0">For Zithas Technologies</p>
                      </div>
                      <div className="col-4">
                        {amendment.signature ? (
                          <img
                            src={amendment.signature}
                            height="50"
                            className="mb-2"
                            alt="Client signature"
                          />
                        ) : (
                          <div className="sign-box">Signature</div>
                        )}
                        <h5 className="mb-0 mt-2">{amendment.signed_name}</h5>
                        <p className="mb-0">For {amendment.company_name}</p>
                        <p style={{ fontSize: "12px", color: "#74788d" }}>
                          Signed{" "}
                          {moment(amendment.signed_at).format(
                            "DD MMM YYYY, hh:mm A",
                          )}
                        </p>
                      </div>
                    </Row>
                  )}
                </div>
              </div>

              {/* Right summary panel */}
              <div className="col-lg-3">
                <div className="bg-white p-3 shadow">
                  <span
                    className="badge d-inline-block mb-3"
                    style={{
                      background: badge.bg,
                      color: "#fff",
                      fontSize: "12px",
                    }}
                  >
                    {badge.label}
                  </span>
                  <h6 className="mb-1">Amendment</h6>
                  <p className="mb-3">
                    {amendment.amendment_ref || amendment.amendment_no}
                  </p>

                  <h6 className="mb-1">Change Reason</h6>
                  <p className="mb-3">{amendment.change_reason || "—"}</p>

                  <h6 className="mb-1">Effective Date</h6>
                  <p className="mb-3">
                    {amendment.effective_date
                      ? moment(amendment.effective_date).format("DD MMM YYYY")
                      : "—"}
                  </p>

                  {Number(amendment.fee_change) > 0 && (
                    <>
                      <h6 className="mb-1">Fee Change</h6>
                      <p className="mb-3">
                        {amendment.fee_direction === "add" ? "+" : "-"}
                        {amendment.currency_symbol}
                        {Number(amendment.fee_change).toLocaleString()}
                      </p>
                    </>
                  )}

                  {Number(amendment.hours_change) > 0 && (
                    <>
                      <h6 className="mb-1">Hours Change</h6>
                      <p className="mb-3">
                        {Number(amendment.hours_change)} hrs
                      </p>
                    </>
                  )}

                  <hr />
                  <p style={{ fontSize: "11px", color: "#74788d" }}>
                    Created by {amendment.created_by_name || "—"} ·{" "}
                    {moment(amendment.created_at).format("DD MMM YYYY")}
                  </p>
                  {amendment.status === "signed" && (
                    <p style={{ fontSize: "11px", color: "#34c38f" }}>
                      Signed by {amendment.signed_name} ·{" "}
                      {moment(amendment.signed_at).format("DD MMM YYYY")}
                    </p>
                  )}
                  {amendment.status === "rejected" && (
                    <p style={{ fontSize: "11px", color: "#f46a6a" }}>
                      Rejected by {amendment.rejected_by_name || "—"} ·{" "}
                      {moment(amendment.internal_rejected_at).format(
                        "DD MMM YYYY",
                      )}
                    </p>
                  )}
                </div>
              </div>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ViewContractAmendment;
